import { useTranslations } from "@/lib/useTranslations";

import { Button } from "@/components/ui/Button";
import {
  EditorialDeleteConfirmDialog,
  useEditorialDeleteConfirm,
} from "@/components/ui/admin/EditorialDeleteConfirm";
import type { EventEditStatusValue } from "@/core/services/types/event.types";
import {
  getArticleStatusActions,
  type ArticleStatusAction,
  type ArticleStatusActionVariant,
} from "@/core/services/types/article.types";

interface EventStatusActionsProps {
  currentStatus: EventEditStatusValue;
  itemLabel: string;
  pendingTarget: EventEditStatusValue | null;
  isPending: boolean;
  canPublish?: boolean;
  canDelete?: boolean;
  publishDisabled?: boolean;
  onAction: (targetStatus: EventEditStatusValue) => void;
  onDelete: () => Promise<void>;
}

export function EventStatusActions({
  currentStatus,
  itemLabel,
  pendingTarget,
  isPending,
  canPublish = true,
  canDelete = false,
  publishDisabled = false,
  onAction,
  onDelete,
}: EventStatusActionsProps) {
  const t = useTranslations("events.edit.statusActions");
  const tEdit = useTranslations("events.edit");
  const tCommon = useTranslations("common");
  const actions = getArticleStatusActions(currentStatus, {
    canPublish,
    canDelete,
    publishDisabled,
  });
  const deleteConfirm = useEditorialDeleteConfirm(onDelete);

  if (actions.length === 0) return null;

  function handleActionClick(action: ArticleStatusAction) {
    if (action.labelKey === "delete") {
      deleteConfirm.requestDelete();
      return;
    }

    if (action.target) {
      onAction(action.target);
    }
  }

  return (
    <>
      <div className="space-y-2">
        <p className="text-sm font-semibold text-whart-text">{t("label")}</p>
        <div className="flex flex-col gap-2">
          {actions.map((action) => (
            <StatusActionButton
              key={action.labelKey}
              label={t(action.labelKey)}
              variant={action.variant}
              isLoading={
                action.labelKey === "delete"
                  ? deleteConfirm.isDeleting
                  : isPending && pendingTarget === action.target
              }
              disabled={
                isPending ||
                deleteConfirm.isDeleting ||
                Boolean(action.disabled)
              }
              disabledHint={
                action.disabled && action.disabledHintKey
                  ? tEdit(action.disabledHintKey)
                  : undefined
              }
              onClick={() => handleActionClick(action)}
            />
          ))}
        </div>
      </div>

      <EditorialDeleteConfirmDialog
        open={deleteConfirm.open}
        title={t("deleteConfirmTitle")}
        message={t("deleteConfirmMessage", { name: itemLabel })}
        cancelLabel={tCommon("cancel")}
        confirmLabel={t("delete")}
        deletingLabel={t("deleting")}
        error={deleteConfirm.error}
        isDeleting={deleteConfirm.isDeleting}
        onClose={deleteConfirm.closeDialog}
        onConfirm={() => void deleteConfirm.confirmDelete()}
      />
    </>
  );
}

function StatusActionButton({
  label,
  variant,
  isLoading,
  disabled,
  disabledHint,
  onClick,
}: {
  label: string;
  variant: ArticleStatusActionVariant;
  isLoading: boolean;
  disabled: boolean;
  disabledHint?: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant={variant}
      disabled={disabled}
      aria-busy={isLoading}
      title={disabledHint}
      onClick={onClick}
      className="inline-flex w-full items-center justify-center gap-2"
    >
      {isLoading ? <ActionSpinner variant={variant} /> : null}
      {label}
    </Button>
  );
}

function ActionSpinner({ variant }: { variant: ArticleStatusActionVariant }) {
  const ringColor =
    variant === "danger" ? "border-white" : "border-zinc-900";

  return (
    <span
      aria-hidden
      className={`inline-block size-3.5 animate-spin rounded-full border-2 ${ringColor} border-r-transparent`}
    />
  );
}
